import {
  PollChargeCardApplicationError,
  PollChargeCardApplicationNextActionEnum,
  PollChargeCardApplicationStatusResult,
  PollChargeCardApplicationSuccess,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';
import GetChargeCardApplication from 'services/graphql/queries/GetChargeCardApplicationStatus';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useQuery } from 'urql';

export const MAX_ATTEMPTS = 5;
export const POLL_INTERVAL = 5000;

interface UsePollingResult {
  fetching: boolean;
  error?: PollChargeCardApplicationError | null;
  isChecking: boolean;
  setIsChecking: (isChecking: boolean) => void;
}

const usePolling = (id: string, isBelieveDay0M2: boolean): UsePollingResult => {
  const router = useRouter();
  const [isPaused, setIsPaused] = useState(true);
  const [isChecking, setIsChecking] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [{ data, fetching, error }, refetch] = useQuery<{
    pollChargeCardApplicationStatus: PollChargeCardApplicationStatusResult;
  }>({
    query: GetChargeCardApplication,
    variables: { id },
    requestPolicy: 'network-only',
    pause: isPaused,
  });

  const initiateRefetch = useCallback(() => {
    setIsPaused(false);
    refetch();
  }, [setIsPaused, refetch]);

  const subcategory = isBelieveDay0M2
    ? 'believe_day_0_application'
    : 'believe_application';

  const varoBelieveApproveAnalytics = usePressableAnalytics({
    analyticsId: 'varoBelieveApplicationApprove',
    customAttributes: { subcategory },
  });

  const varoBelieveReviewAnalytics = usePressableAnalytics({
    analyticsId: 'varoBelieveReviewAnalytics',
    customAttributes: { subcategory },
  });

  const varoBelieveDeclinedAnalytics = usePressableAnalytics({
    analyticsId: 'varoBelieveDeclinedAnalytics',
    customAttributes: { subcategory },
  });

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const clearPollingInterval = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handlePollStatus = () => {
      if (
        (data?.pollChargeCardApplicationStatus as PollChargeCardApplicationSuccess) &&
        !fetching &&
        !error
      ) {
        const nextAction = (
          data?.pollChargeCardApplicationStatus as PollChargeCardApplicationSuccess
        ).nextAction;
        const cardId = (
          data?.pollChargeCardApplicationStatus as PollChargeCardApplicationSuccess
        ).cardId;
        setIsChecking(true);
        if (nextAction === PollChargeCardApplicationNextActionEnum.Approved) {
          varoBelieveApproveAnalytics();
          router.replace({
            pathname: isBelieveDay0M2
              ? '/(authenticated)/believe/apply/approved-application'
              : '/believe/apply/approved',
            params: {
              cardId: cardId,
            },
          });
        } else if (
          nextAction === PollChargeCardApplicationNextActionEnum.Declined
        ) {
          varoBelieveDeclinedAnalytics();
          router.replace({
            pathname: '/believe/apply/cardApplicationError',
            params: { isBelieveDay0M2: String(isBelieveDay0M2) },
          });
        } else if (
          nextAction === PollChargeCardApplicationNextActionEnum.Review
        ) {
          varoBelieveReviewAnalytics();
          router.replace({
            pathname: '/believe/apply/review',
            params: { isBelieveDay0M2: String(isBelieveDay0M2) },
          });
        } else if (
          nextAction === PollChargeCardApplicationNextActionEnum.Retry &&
          attempts === MAX_ATTEMPTS - 1
        ) {
          varoBelieveReviewAnalytics();
          router.replace({
            pathname: '/believe/apply/review',
            params: { isBelieveDay0M2: String(isBelieveDay0M2) },
          });
        }
      } else if (error && attempts === MAX_ATTEMPTS - 1) {
        varoBelieveDeclinedAnalytics();
        router.replace({
          pathname: '/believe/apply/cardApplicationError',
          params: { isBelieveDay0M2: String(isBelieveDay0M2) },
        });
      }
    };

    if (isChecking) {
      if (attempts < MAX_ATTEMPTS) {
        intervalId = setInterval(() => {
          initiateRefetch();
          setAttempts((prev) => prev + 1);
        }, POLL_INTERVAL);
      } else {
        clearPollingInterval();
      }

      handlePollStatus();
    }

    return () => {
      clearPollingInterval();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isChecking,
    attempts,
    data,
    initiateRefetch,
    error,
    fetching,
    isBelieveDay0M2,
    router,
  ]);

  return {
    fetching,
    setIsChecking,
    isChecking,
  };
};

export default usePolling;
